import React, { useState, useRef } from 'react';
import { ArrowLeft, ArrowRight, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatPhoneNumber } from '../lib/phoneFormat';
import { getRecaptchaToken, isRecaptchaEnabled } from '../lib/recaptcha';
import { optimizeImage } from '../utils/cloudinary';
import '../styles/airacademy.css';

const AirAcademyPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const formRef = useRef(null);

  const images = {
    hero: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/a2od9sbz_Ayla_iFLY_Flying1500mb.png', { width: 1200 }),
    lyraTunnel: '/images/airacademy/lyra_tunnel.jpg'
  };

  const [formData, setFormData] = useState({
    parent_name: '',
    child_name: '',
    child_age: '',
    school: '',
    email: '',
    phone: '',
    flown_ifly: '',
    aerial_experience: '',
    interest_reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const interestOptions = [
    'Tunnel flying',
    'Aerial arts',
    'Both',
    "Honestly, my kid just needs something different"
  ];

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.flown_ifly || !formData.aerial_experience || !formData.interest_reason) {
      setError('Please answer all the questions so Cal knows where your kid is starting from.');
      return;
    }
    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('air_academy_form');
      const response = await fetch(`${API_URL}/api/air-academy/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, website: honeypot, recaptcha_token: recaptchaToken })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      setIsSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="airacademy-page" data-testid="air-academy-page">
      {/* HERO */}
      <section className="aa-hero">
        <div className="aa-hero-media">
          <img src={images.hero} alt="Kid tunnel flying at iFLY" />
          <div className="aa-hero-overlay"></div>
        </div>
        <div className="aa-hero-content">
          <Link to="/aerial" className="aa-back-link" data-testid="aa-back-link">
            <ArrowLeft size={18} />
            Aerial Kids
          </Link>
          <span className="aa-pilot-badge">Fall/Winter 2026 Pilot</span>
          <h1 className="aa-hero-title">Go Cal Fit<br /><span>Air Academy</span></h1>
          <p className="aa-hero-tagline">Tunnel flying + aerial arts. One new kind of afterschool sport.</p>
          <div className="aa-hero-facts">
            <span>Ages 8–12</span>
            <span className="aa-dot">·</span>
            <span>Tuesdays · 3:15–5:30pm</span>
            <span className="aa-dot">·</span>
            <span>iFLY Wilmington</span>
          </div>
          <Button className="aa-cta-btn" onClick={scrollToForm} data-testid="aa-hero-cta">
            Tell Me More About Air Academy
            <ArrowRight size={18} />
          </Button>
        </div>
      </section>

      {/* THEY FLY. THEY TRAIN. THEY PROGRESS. */}
      <section className="aa-section aa-flytrain">
        <h2 className="aa-section-title">They fly. They train. They progress.</h2>
        <div className="aa-reel-wrap">
          <video
            src="/videos/air_academy_reel.mp4"
            poster="/images/airacademy/reel_poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="aa-train-video"
          />
          <span className="aa-media-caption">Real footage: our kids flying — and training aerial — at iFLY Wilmington</span>
        </div>
        <div className="aa-flytrain-grid">
          <div className="aa-flytrain-text">
            <p>
              Athletes fly every week at iFLY, working through the{' '}
              <a
                href="https://www.tunnelflight.com/skills/"
                target="_blank"
                rel="noopener noreferrer"
                className="aa-iba-link"
                data-testid="aa-iba-link"
              >
                International Bodyflight Association (IBA) flight progression
                <ExternalLink size={14} />
              </a>{' '}
              — learning real tunnel-flying skills, body positions and control.
            </p>
          </div>
          <div className="aa-flytrain-text">
            <p>
              Between flights, GoCalFit brings our mobile aerial equipment right into iFLY for aerial arts + strength training — developing the strength, mobility, spatial awareness and body control that support their skills both in and out of the tunnel.
            </p>
          </div>
        </div>

        {/* Progression strip */}
        <div className="aa-progression" data-testid="aa-progression">
          <div className="aa-prog-step"><span className="aa-prog-num">01</span>Fly</div>
          <ArrowRight className="aa-prog-arrow" size={20} />
          <div className="aa-prog-step"><span className="aa-prog-num">02</span>Train</div>
          <ArrowRight className="aa-prog-arrow" size={20} />
          <div className="aa-prog-step"><span className="aa-prog-num">03</span>Progress</div>
          <ArrowRight className="aa-prog-arrow" size={20} />
          <div className="aa-prog-step aa-prog-final"><span className="aa-prog-num">04</span>Fly Together</div>
        </div>
        <p className="aa-progression-note">
          As kids reach the appropriate IBA + GoCalFit skill levels, we'll begin bringing those skills together through group tunnel-flying choreography.
        </p>

        <div className="aa-callout">
          <p>This isn't a weekly trip to iFLY.</p>
          <p className="aa-callout-strong">They fly. They train. They progress.</p>
        </div>
      </section>

      {/* WHY TUNNEL + AERIAL */}
      <section className="aa-section aa-why">
        <div className="aa-why-grid">
          <div className="aa-why-image">
            <img src={images.lyraTunnel} alt="Kid training on the lyra in front of the iFLY tunnel" />
          </div>
          <div className="aa-why-text">
            <h2 className="aa-section-title">Why tunnel flying + aerial?</h2>
            <p>
              Both ask kids to learn how to control their bodies in three-dimensional space — building strength, spatial awareness, coordination, confidence and body control.
            </p>
            <p>
              Air Academy gives them the opportunity to develop those skills across two disciplines and see how what they learn in one transfers to the other.
            </p>
          </div>
        </div>
      </section>

      {/* PILOT INFO CARD */}
      <section className="aa-section aa-pilot">
        <div className="aa-pilot-card" data-testid="aa-pilot-card">
          <span className="aa-pilot-badge">Fall/Winter 2026 Pilot</span>
          <div className="aa-pilot-facts">
            <div className="aa-pilot-fact">
              <span className="aa-fact-label">When</span>
              <span className="aa-fact-value">Late September – December 2026</span>
            </div>
            <div className="aa-pilot-fact">
              <span className="aa-fact-label">Day & Time</span>
              <span className="aa-fact-value">Tuesdays · 3:15–5:30pm</span>
            </div>
            <div className="aa-pilot-fact">
              <span className="aa-fact-label">Ages</span>
              <span className="aa-fact-value">8–12</span>
            </div>
            <div className="aa-pilot-fact">
              <span className="aa-fact-label">Team Size</span>
              <span className="aa-fact-value">Founding team of six</span>
            </div>
          </div>

          <div className="aa-pilot-includes">
            <p className="aa-includes-label">Every training day includes:</p>
            <ul>
              <li><Check size={16} /> Tunnel flying at iFLY Wilmington</li>
              <li><Check size={16} /> IBA flight progression</li>
              <li><Check size={16} /> Aerial arts training</li>
              <li><Check size={16} /> GoCalFit strength + body-control training</li>
              <li><Check size={16} /> Progress toward group flying as skills develop</li>
            </ul>
          </div>

          <p className="aa-beginners">
            <strong>Beginners welcome — and encouraged.</strong><br />
            No previous tunnel flying or aerial experience is required. Every athlete trains and progresses at their individual skill level.
          </p>

          <div className="aa-getting-there">
            <p className="aa-includes-label">Getting there</p>
            <p>
              School pickup may be available if the founding group attends the same school or two nearby schools. Otherwise, families will drop off directly at iFLY Wilmington.
            </p>
            <p>The Fall/Winter season begins once the founding team of six is confirmed.</p>
          </div>
        </div>
      </section>

      {/* INTEREST FORM */}
      <section className="aa-section aa-form-section" ref={formRef} id="interest-form">
        {isSubmitted ? (
          <div className="aa-success" data-testid="aa-form-success">
            <div className="aa-success-icon"><Check size={44} /></div>
            <h2>You're on the flight list.</h2>
            <p>I'll be in touch with the full season details as the founding team comes together.</p>
            <Link to="/aerial">
              <Button className="aa-cta-btn aa-cta-secondary">Back to Aerial Kids</Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="aa-section-title">Could Air Academy be your kid's thing?</h2>
            <p className="aa-form-intro">
              We're selecting the founding team of six now. Join the interest list and Cal will send you the full Fall/Winter season details, including tuition, dates and transportation options.
            </p>
            <p className="aa-form-note">Joining the list doesn't commit you to a spot.</p>

            <form onSubmit={handleSubmit} className="aa-form" data-testid="aa-interest-form">
              {/* Honeypot — hidden from real users, bots auto-fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              {error && <div className="aa-form-error" data-testid="aa-form-error">{error}</div>}

              <div className="aa-form-row">
                <div className="aa-form-group">
                  <label>Parent/Guardian Name</label>
                  <Input
                    name="parent_name"
                    value={formData.parent_name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    required
                    data-testid="aa-input-parent-name"
                  />
                </div>
                <div className="aa-form-group">
                  <label>Child's Name</label>
                  <Input
                    name="child_name"
                    value={formData.child_name}
                    onChange={handleInputChange}
                    placeholder="Child's name"
                    required
                    data-testid="aa-input-child-name"
                  />
                </div>
              </div>

              <div className="aa-form-row">
                <div className="aa-form-group aa-form-group-small">
                  <label>Child's Age</label>
                  <select
                    name="child_age"
                    value={formData.child_age}
                    onChange={handleInputChange}
                    required
                    className="aa-select"
                    data-testid="aa-select-child-age"
                  >
                    <option value="">Age</option>
                    {['8', '9', '10', '11', '12'].map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
                <div className="aa-form-group">
                  <label>School</label>
                  <Input
                    name="school"
                    value={formData.school}
                    onChange={handleInputChange}
                    placeholder="Child's school"
                    required
                    data-testid="aa-input-school"
                  />
                </div>
              </div>

              <div className="aa-form-row">
                <div className="aa-form-group">
                  <label>Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@email.com"
                    required
                    data-testid="aa-input-email"
                  />
                </div>
                <div className="aa-form-group">
                  <label>Mobile Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(000) 000-0000"
                    required
                    data-testid="aa-input-phone"
                  />
                </div>
              </div>

              <div className="aa-form-group">
                <label>Has your child flown at iFLY before?</label>
                <div className="aa-toggle-group" data-testid="aa-toggle-flown-ifly">
                  {['Yes', 'No'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`aa-toggle-btn ${formData.flown_ifly === opt ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, flown_ifly: opt }))}
                      data-testid={`aa-flown-ifly-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="aa-form-group">
                <label>Does your child have aerial, gymnastics, dance or similar experience?</label>
                <div className="aa-toggle-group" data-testid="aa-toggle-experience">
                  {['Yes', 'No'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`aa-toggle-btn ${formData.aerial_experience === opt ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, aerial_experience: opt }))}
                      data-testid={`aa-experience-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="aa-form-group">
                <label>What interests you most about Air Academy?</label>
                <div className="aa-toggle-group aa-toggle-stack" data-testid="aa-toggle-interest">
                  {interestOptions.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`aa-toggle-btn ${formData.interest_reason === opt ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, interest_reason: opt }))}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className="aa-cta-btn aa-submit-btn"
                disabled={isSubmitting}
                data-testid="aa-form-submit"
              >
                {isSubmitting ? 'Sending...' : 'Tell Me More About Air Academy'}
                <ArrowRight size={18} />
              </Button>

              <p className="aa-form-footer">
                Interest list only — no payment, no commitment.
                {isRecaptchaEnabled() && (
                  <> Protected by reCAPTCHA — Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="aa-iba-link">Privacy</a> & <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="aa-iba-link">Terms</a> apply.</>
                )}
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

export default AirAcademyPage;
